/**
 * 리모컨 묶음(PICNIC-MB-FIELD-001 v0.2 §4 유형 R · 2026-10-05) — 보드가 놀이터의 캐릭터를 조종한다.
 * L34 미로 정원 · X02 외줄타기 · X01 마라톤 코스 · X08 비밀의 문 · L33 손목시계 탐험. 순수 상태(DOM · 시계 없음) — 지난 시간 ms 와 그때의 보드 줄.
 * 판정은 놀이터가 본 것(기울기 · 흔들기 · 로고 · 버튼 · 화면 · 온도)으로만 한다 — 아이 프로그램의 속은 모른다.
 */
import type { MbFrame } from "./live";
import { TILT_MG, isBlank } from "./live";
import { bitsOfLeds } from "./font";

const litCount = (leds: readonly number[]) => leds.filter((v) => v > 0).length;

// ── L34 미로 정원 — 기울여 7×7 정원 미로를 빠져나간다 ─────────────────────

/** 정원 미로 — # 나무 · S 출발 · E 도착(한 줄 = 한 행) */
export const MAZE = [
  "S.#....",
  ".##.##.",
  "...#...",
  "##.#.##",
  "...#...",
  ".###.#.",
  ".....#E",
] as const;
export const MAZE_N = 7;
export const MAZE_STEP_MS = 400;

export interface Maze { readonly r: number; readonly c: number; readonly clock: number; readonly bumps: number; readonly done: boolean }
export const initialMaze = (): Maze => ({ r: 0, c: 0, clock: 0, bumps: 0, done: false });
const wall = (r: number, c: number) => r < 0 || c < 0 || r >= MAZE_N || c >= MAZE_N || MAZE[r]![c] === "#";

/** 기울인 쪽으로 0.4초마다 한 칸 — 대각선은 더 크게 기운 쪽 · 나무에 막히면 그대로(부딪힌 수만 센다) */
export function stepMaze(s: Maze, dtMs: number, frame: MbFrame | null): Maze {
  if (s.done || !frame) return s;
  const clock = s.clock + dtMs;
  if (clock < MAZE_STEP_MS) return { ...s, clock };
  const { ax, ay } = frame;
  const big = Math.max(Math.abs(ax), Math.abs(ay));
  if (big < TILT_MG) return { ...s, clock: 0 };
  const [dr, dc] = Math.abs(ay) >= Math.abs(ax) ? [ay < 0 ? -1 : 1, 0] : [0, ax < 0 ? -1 : 1];
  const r = s.r + dr, c = s.c + dc;
  if (wall(r, c)) return { ...s, clock: 0, bumps: s.bumps + 1 };
  return { r, c, clock: 0, bumps: s.bumps, done: MAZE[r]![c] === "E" };
}

// ── X02 외줄타기 — 수평을 지키는 동안 건너고, 기울면 떨어진다 ──────────────

export const BALANCE_LEVEL_MG = 200;
export const BALANCE_FALL_MG = 450;
export const BALANCE_GOAL_MS = 5000;
export const BALANCE_MAX_MS = 60000;

export interface Balance { readonly levelMs: number; readonly best: number; readonly falls: number; readonly done: boolean }
export const initialBalance = (): Balance => ({ levelMs: 0, best: 0, falls: 0, done: false });

export function stepBalance(s: Balance, dtMs: number, frame: MbFrame | null): Balance {
  // 해낸 뒤에도 별 셋(15초)까지 가장 오래 버틴 시간을 잰다 — 해냄은 한 번 켜지면 그대로
  if (!frame || s.best >= BALANCE_MAX_MS) return s;
  const tilt = Math.max(Math.abs(frame.ax), Math.abs(frame.ay));
  if (tilt >= BALANCE_FALL_MG) return { ...s, levelMs: 0, falls: s.levelMs > 0 ? s.falls + 1 : s.falls };
  if (tilt > BALANCE_LEVEL_MG) return s;   // 살짝 흔들림 — 멈춰 서 있다(떨어지지는 않는다)
  const levelMs = s.levelMs + dtMs;
  return { ...s, levelMs, best: Math.max(s.best, levelMs), done: s.done || levelMs >= BALANCE_GOAL_MS };
}

// ── X01 마라톤 코스 — 걸음 막대(켜진 칸) = 코스 진행 · 가득 차면 도착 ──────────

export const MARATHON_LEN = 25;
export const MARATHON_HOLD_MS = 500;

export interface Marathon { readonly pos: number; readonly steps: number; readonly wasShake: boolean; readonly held: number; readonly done: boolean }
export const initialMarathon = (): Marathon => ({ pos: 0, steps: 0, wasShake: false, held: 0, done: false });

export function stepMarathon(s: Marathon, dtMs: number, frame: MbFrame | null): Marathon {
  if (s.done || !frame) return s;
  const pos = litCount(frame.leds);
  const steps = s.steps + (frame.shake && !s.wasShake ? 1 : 0);
  const held = pos >= MARATHON_LEN ? s.held + dtMs : 0;
  return { pos, steps, wasShake: frame.shake, held, done: held >= MARATHON_HOLD_MS };
}

// ── X08 비밀의 문 — 왼쪽 기울이기 → 흔들기 → 로고, 틀리면 처음부터 ──────────

export type DoorMove = "left" | "shake" | "logo";
export const DOOR_CODE: readonly DoorMove[] = ["left", "shake", "logo"];

export interface Door {
  readonly i: number;
  readonly resets: number;
  readonly prev: { left: boolean; shake: boolean; logo: boolean; a: boolean; b: boolean } | null;
}
export const initialDoor = (): Door => ({ i: 0, resets: 0, prev: null });

/**
 * 몸짓이 '시작되는 순간'만 센다. 흔드는 동안 보드가 좌우로 기우는 것 · 흔들기가 이어지는 것은 틀린 것으로 치지 않는다(앞 단계의 몸짓은 무시).
 * 순서에 없는 몸짓 · 다음 차례가 아닌 몸짓(로고를 먼저 · A/B 단추)은 처음부터.
 */
export function stepDoor(s: Door, frame: MbFrame | null): Door {
  if (!frame || s.i >= DOOR_CODE.length) return s;
  const now = { left: frame.ax <= -TILT_MG, shake: frame.shake, logo: frame.logo, a: frame.a, b: frame.b };
  const p = s.prev ?? { left: false, shake: false, logo: false, a: false, b: false };
  const began = (k: keyof typeof now) => now[k] && !p[k];
  let i = s.i, resets = s.resets;
  const want = DOOR_CODE[i]!;
  const moves: (DoorMove | "button")[] = [];
  if (began("left")) moves.push("left");
  if (began("shake")) moves.push("shake");
  if (began("logo")) moves.push("logo");
  if (began("a") || began("b")) moves.push("button");
  for (const m of moves) {
    if (m === want) { i++; break; }
    const earlier = m !== "button" && DOOR_CODE.indexOf(m) < i;   // 이미 지난 몸짓의 되풀이(흔드는 동안 기울기 등)
    if (!earlier) { i = 0; resets++; break; }
  }
  return { i, resets, prev: now };
}

// ── L33 손목시계 탐험 — 모드 3개를 다 써서 미션 3개 ──────────────────────

export const WATCH_SCREENS_GOAL = 3;
export const WATCH_WARM_C = 28;
export const WATCH_WARM_MS = 1000;
export const WATCH_STEPS_GOAL = 5;

export interface Watch {
  /** A · B 로 바꾼 뒤 본 서로 다른 화면(모드 바꾸기) */
  readonly screens: readonly string[];
  readonly warmMs: number;
  readonly warm: boolean;
  readonly steps: number;
  readonly prev: { a: boolean; b: boolean; shake: boolean } | null;
  /** 단추를 누른 뒤 아직 새 화면을 기다린다 */
  readonly armed: boolean;
}
export const initialWatch = (): Watch => ({ screens: [], warmMs: 0, warm: false, steps: 0, prev: null, armed: false });
export const watchMissions = (s: Watch) => ({
  modes: s.screens.length >= WATCH_SCREENS_GOAL,
  warm: s.warm,
  steps: s.steps >= WATCH_STEPS_GOAL,
});
export const watchDone = (s: Watch) => { const m = watchMissions(s); return m.modes && m.warm && m.steps; };

/**
 * 모드 바꾸기: A · B 를 누른 뒤 처음 나온 화면이 전에 없던 것이면 하나 더(3가지).
 * 따뜻한 곳: 온도가 28° 넘는 채 보드가 무언가 보이기를 1초(온도 모드로 재는 중) — 센서 값이 없으면(진짜 보드) 이 미션은 열리지 않는다.
 * 걸음 세기: 보드가 무언가 보이는 동안 흔든 수(5걸음).
 */
export function stepWatch(s: Watch, dtMs: number, frame: MbFrame | null): Watch {
  if (!frame) return s;
  const p = s.prev ?? { a: false, b: false, shake: false };
  const pressed = (frame.a && !p.a) || (frame.b && !p.b);
  const showing = !isBlank(frame.leds);
  let { screens, armed } = s;
  if (pressed) armed = true;
  else if (armed && showing) {
    const bits = bitsOfLeds(frame.leds);
    if (!screens.includes(bits)) screens = [...screens, bits].slice(-8);
    armed = false;
  }
  const temp = frame.sensors?.temp;
  const warmMs = temp !== undefined && temp >= WATCH_WARM_C && showing ? s.warmMs + dtMs : 0;
  const steps = s.steps + (frame.shake && !p.shake && showing ? 1 : 0);
  return { screens, armed, warmMs, warm: s.warm || warmMs >= WATCH_WARM_MS, steps, prev: { a: frame.a, b: frame.b, shake: frame.shake } };
}

/** 리모컨 묶음 한 덩어리 */
export interface Remotes { readonly maze: Maze; readonly balance: Balance; readonly marathon: Marathon; readonly door: Door; readonly watch: Watch }
export const initialRemotes = (): Remotes => ({ maze: initialMaze(), balance: initialBalance(), marathon: initialMarathon(), door: initialDoor(), watch: initialWatch() });
export function stepRemotes(s: Remotes, dtMs: number, frame: MbFrame | null): Remotes {
  return {
    maze: stepMaze(s.maze, dtMs, frame),
    balance: stepBalance(s.balance, dtMs, frame),
    marathon: stepMarathon(s.marathon, dtMs, frame),
    door: stepDoor(s.door, frame),
    watch: stepWatch(s.watch, dtMs, frame),
  };
}

